<template>
  <div class="col-12">
    <SectionTitle :content="title" :has-line="true" />
    <div v-if="showQuestionnaireForStudent" class="col-12 mt-4 mb-4 mx-3">
      <validation-provider
        v-if="hasValidationPending"
        v-slot="{ errors }"
        :rules="{ required: field.questionnaireStatus == 'Pendiente' }"
      >
        <CustomTextArea
          v-show="false"
          id="txtEmptyString"
          v-model="emptyString"
        />
        <error
          v-if="errors.length > 0"
          message="Favor de completar el Cuestionario de Intervención."
        />
      </validation-provider>
      <CustomStatusLink
        v-if="canShowInterventionQuestionnaire"
        :title="'Cuestionario de Intervención'"
        :status="field.questionnaireStatus"
        :params="statusLinkParams"
        :use-link-name="true"
        :link-to="'peiEvaluationServiceIntervention'"
      />
    </div>
    <div class="row mx-3">
      <div class="col-md-4 col-sm-12">
        <ValidationProvider
          v-slot="{ errors }"
          name="requestDate"
          rules="required"
        >
          <CustomDatePicker
            id="txtDatePicker"
            v-model="value.dateEligibility"
            :error="errors[0]"
            custom-error-msg="Favor de seleccionar una fecha para la solicitud."
            :max-date="new Date()"
            :min-date="dateRegistration"
            label="Fecha:"
            placeholder="DD/MM/YYYY"
            :disabled="
              disableEligibilityDate ||
                disabled ||
                (dateRegistration ? false : true)
            "
            class="w-100"
          />
        </ValidationProvider>
      </div>
      <div class="col-md-4 col-sm-12">
        <strong class="ml-3">Elegible:</strong>
        <ValidationProvider
          v-slot="{ errors }"
          name="isElegibility"
          rules="required"
        >
          <div class="input-group mt-2 ml-0">
            <CustomRadio
              v-model="value.isElegibility"
              :error="errors[0]"
              custom-error-msg="Favor de seleccionar Si o No para continuar. (21043)"
              name="isElegibility"
              :options="field.radioElegibility.options"
              is-inline
              :is-disabled="disabled"
              @input="radioElegibilityChange($event)"
            />
          </div>
        </ValidationProvider>
      </div>
      <div v-if="value.isElegibility" class="col-md-4 col-sm-12">
        <ValidationProvider
          v-slot="{ errors }"
          name="impediment"
          mode="eager"
          rules="required"
        >
          <CustomDropDown
            id="dropDownImpediment"
            v-model="value.impediment"
            class="w-50 d-inline"
            :disabled="disabled"
            :clearable="!disabled"
            label="Impedimento:"
            custom-error-msg="Favor de seleccionar un impedimento para continuar. (21044)"
            :error="errors[0]"
            placeholder="Seleccione"
            fieldkey="id"
            fieldname="name"
            :options="determinationOptions.impediments"
            @input="cleanImpedimentDependency"
          />
        </ValidationProvider>
      </div>
    </div>

    <div class="row">
      <div v-if="value.isElegibility === false" class="col-sm-12 pl-4 ml-4">
        <error
          style="position:relative;right: 2px;"
          is-alert-mode
          message="Al seleccionar que el estudiante No es Elegible el mismo será Egresado."
        />
      </div>
    </div>

    <div class="row mx-3 mt-2">
      <div
        class="col-12"
        v-if="value.isElegibility != null && !value.isElegibility"
      >
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="dissabilityOption"
        >
          <div>
            <CustomRadio
              v-model="value.dissabilityOption"
              :error="errors[0]"
              class="dissability-options"
              custom-error-msg="Favor seleccionar una razón de determinación"
              name="dissabilityOption"
              :options="field.radioDissabilities.options"
              :is-disabled="disabled"
            />
          </div>
        </ValidationProvider>
      </div>
      <div class="col-12 mt-3">
        <ValidationProvider
          v-slot="{ errors }"
          name="comment"
          mode="eager"
          rules="required"
        >
          <CustomTextArea
            id="txtAreaComment"
            v-model="value.comments"
            :disabled="disabled"
            class="eligiblity-determination__text"
            label="Describa el resultado de la determinación de elegibilidad"
            :maxlength="3000"
            :rows="5"
            placeholder="Escriba resultado de la determinación de elegibilidad"
            :error="errors[0]"
            custom-error-msg="Favor de ingresar un comentario para continuar. (21048)"
          />
        </ValidationProvider>
      </div>
    </div>

    <ImpedimentsManager
      class="m-4"
      ref="ImpedimentsManager"
      v-model="impedimentsModel"
      :options="impedimentsOptions"
      :all-impediments="determinationOptions.impediments"
      :selected-impediment-ids="selectedImpedimentIds"
      :is-disabled="disabled"
      :severity-options="determinationOptions.severities"
      :disorders-options="determinationOptions.disorders"
      @add-impediment="btnAddClick"
      @delete-impediment="removeAddImpedimentMultipleItemSelected"
    />

    <div class="row ml-4 pl-1 mt-3">
      <div class="mt-2">
        <strong class="d-inline">
          Cantidad de reuniones que se llevaron a cabo para determinar
          elegibilidad:
        </strong>
      </div>
      <div class="col-md-2 col-sm-12 ml-2">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          name="meetingsQty"
          rules="required"
        >
          <CustomDropDown
            v-model="value.meetingQty"
            class="w-50 d-inline"
            custom-error-msg="Favor de especificar la cantidad de reuniones que se llevaron a cabo para determinar la elegibilidad. (22080)"
            :error="errors[0]"
            placeholder="Seleccione"
            fieldkey="id"
            fieldname="name"
            :options="getMeetingQuantities()"
            :disabled="disabled"
            :clearable="!disabled"
          />
        </ValidationProvider>
      </div>
    </div>

    <div class="row ml-4 pl-1 mt-2">
      <div class="mt-4">
        <strong class="d-inline">
          ¿Fue necesaria la participación de un especialista de la salud?:
        </strong>
      </div>
      <div class="ml-2 mt-4">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="HealthSpecialistNecessary"
        >
          <div>
            <CustomRadio
              v-model="value.isHealthSpecialistNecessary"
              :error="errors[0]"
              custom-error-msg="Favor de contestar si fue necesaria la participación de un especialista de la salud. (22029)"
              name="isHealthSpecialistNecessary"
              :options="field.radioHealthSpecialistNecessary.options"
              is-inline
              :is-disabled="disabled"
              @input="radioHealthSpecialistNecessaryChange($event)"
            />
          </div>
        </ValidationProvider>
      </div>
    </div>

    <div v-if="value.isHealthSpecialistNecessary" class="row ml-4 pl-1 mt-2">
      <div class="mt-4">
        <strong class="d-inline">¿Participó el especialista?</strong>
      </div>
      <div class="ml-2 mt-4">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="isParticipatedSpecialist"
        >
          <div>
            <CustomRadio
              v-model="value.participatedSpecialist"
              :error="errors[0]"
              custom-error-msg="Favor de contestar si el Especialista participó. (22079)"
              name="participatedSpecialist"
              :options="field.radioParticipatedSpecialist.options"
              is-inline
              :is-disabled="disabled"
              @input="radioParticipatedSpecialistChange($event)"
            />
          </div>
        </ValidationProvider>
      </div>
    </div>

    <div class="row ml-4 pl-1 mt-2" v-if="value.isElegibility">
      <eligibility-determination-conditions
        v-model="this.value"
        :conditions="this.determinationOptions.conditions"
        :disabled="disabled"
      />
    </div>

    <div v-show="value.isElegibility" class="row ml-4 pl-1 mt-4">
      <div class="col-0 custom-control custom-checkbox">
        <input
          id="checkboxCertification"
          v-model="value.certification"
          name="checkboxCertification"
          type="checkbox"
          class="custom-control-input"
          :disabled="disabled"
        />
        <label
          for="checkboxCertification"
          class="custom-control-label font regular"
        >
          <span v-if="value.certification != null">
            <i class="ic-check" />
          </span>
        </label>
      </div>
      <div class="col-10">
        <span style="font-weight: 100;">
          Certificamos que en caso de que el niño o joven haya sido considerado
          elegible para servicios de educación especial,
        </span>
        <strong>
          la decisión no se ha basado primordialmente en alguno de los
          siguientes factores: falta de instrucción en lectura o matemáticas o
          dominio limitado del idioma español.
        </strong>
      </div>
    </div>
    <div class="clearfix" />
    <div class="row ml-4 pl-1 mb-0">
      <Error
        v-if="value.isElegibility && !value.certification"
        variant="no-border"
        message="Debe certificar la elegibilidad."
      />
    </div>
  </div>
</template>

<script>
import store from '@/store/index';
import loading from '@/mixin/loading.mixin';
import CustomStatusLink from '@/components/common/CustomStatusLink';
import QuestionnaireTypes from '@/utils/constants/questionnairetypes.js';
import validationMixin from '@/mixin/validation.mixin.js';
import alert from '@/mixin/alert.mixin';
import { ValidationProvider } from 'vee-validate';
import { SectionTitle } from '@/components/pei/shared';
import {
  CustomDatePicker,
  ActionButton,
  CustomDropDown,
  CustomTextArea,
  CustomChipsGroup,
  CustomRadio,
} from '@/components/common';

import Error from 'custom-error';
import StudentStatuses from '@/utils/constants/student/studentStatuses';
import EligibilityDeterminationConditions from './EligibilityDeterminationConditions';
import ImpedimentsManager from './impediments/ContainerManager';

//JSF: TODO Agregar los estatuses de Cuestionario por ID y enviarlo desde el api para
//     tenerlo disponible para la comparacion si el CuestionarioStatusId es Completado
const QuestionnarieStatus = {
  Pendiente: 'Pendiente',
  Completado: 'Completado',
};
const ImpedimentsEnum = {
  MR_DiscapacidadIntelectual: 'MR-Discapacidad Intelectual',
  HI_ProblemaAudicion: 'HI-Problema de Audición',
  AUT_Autismo: 'AUT-Autismo',
  MD_ImpedimentosMultiples: 'MD-Impedimentos Múltiples',
};
export default {
  // * Se supone que este remplazara al existente pero se debe hacer la implementaciones correspondientes
  name: 'EligibilityDeterminationV2',
  mixins: [loading, alert, validationMixin],
  components: {
    ActionButton,
    CustomTextArea,
    CustomDropDown,
    CustomDatePicker,
    ValidationProvider,
    SectionTitle,
    CustomChipsGroup,
    CustomRadio,
    CustomStatusLink,
    Error,
    EligibilityDeterminationConditions,
    ImpedimentsManager,
  },
  data() {
    return {
      name: 'EligibilityDetermination',
      statusLinkParams: {
        from: 'Pos-Registro',
        questionnaireTypes: [QuestionnaireTypes.PrePruebaIntervencion],
        place: ['Inicio', 'Estudiante', 'Cuestionario de Intervención'],
        linkBack: '/student/registrygeneral',
      },
      emptyString: '',
      studentSie: store.getters['$_student/StudentSie'],
      title: 'Determinación de Elegibilidad',
      showMultipleImpedimentSection: false,
      field: {
        showQuestionnaireForStudent: false,
        questionnaireStatus: '',
        radioElegibility: {
          options: [
            { label: 'Si', value: true },
            { label: 'No', value: false },
          ],
        },
        radioHealthSpecialistNecessary: {
          options: [
            { label: 'Si', value: true },
            { label: 'No', value: false },
          ],
        },
        radioParticipatedSpecialist: {
          options: [
            { label: 'Si', value: true },
            { label: 'No', value: false },
          ],
        },
        dropDownImpedimentMultiple: {
          required: false,
        },
        radioDissabilities: {
          options: [
            {
              label:
                'El estudiante no presenta una discapacidad según se define en la Ley IDEA.',
              value: 1,
            },
            {
              label:
                'El estudiante presenta una discapacidad, pero esta no limita negativamente su educación.',
              value: 2,
            },
            {
              label:
                'El estudiante presenta una discapacidad que lo limita negativamente su educación.',
              value: 3,
            },
          ],
        },
      },
      impedimentsModel: {
        severity: null,
        isPartialHearing: null,
        disorders: null,
      },
      impedimentsOptions: [],
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => {
        return {
          dateEligibility: null, // type: [Date, String],
          isElegibility: null, //Bool
          dissabilityOption: null, //int
          comments: '', //String
          meetingQty: null, //Object
          isHealthSpecialistNecessary: null, //Bool
          isConditionsInProcessNecessary: null, //Bool
          participatedSpecialist: null, //Bool
          certification: null, //Bool
          impediment: null, //Object
          severity: null, //Object
          disorders: null, //Object
          partialHearing: null, //Object
          //Multiple
          impedimentMultiple: {
            impediment: null,
            selectedItems: [],
          }, //Object
          severityMultiple: null,
          disordersMultiple: null, //Object
          partialHearingMultiple: null, //Object
          eligibilityDeterminationConditions: {
            condition: null,
            selectedItems: [],
          }, //Object
        };
      },
    },
    placementCommitteId: {
      type: [String, Number],
      default: () => null,
    },
    dateRegistration: {
      type: Date,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    showQuestionnaireForStudent: {
      type: Boolean,
      default: false,
    },
    showInterventionQuestionnaire: {
      type: Boolean,
      default: () => true,
    },
    determinationOptions: {
      type: Object,
      required: true,
      default: () => {
        return {
          impediments: [],
          multipleImpediments: [],
          severities: [],
          disorders: [],
          conditions: [],
        };
      },
    },
    disableEligibilityDate: {
      type: Boolean,
      default: true,
    },
    // !Deprecated but first we need change implementation in Student
    calledFrom: {
      type: String,
      default: () => new String(),
    },
    hasValidationPending: {
      type: Boolean,
      default: () => false,
    },
    registry: {
      type: Object,
      default: () => null,
    },
    studentRegistryStatus: {
      type: Number,
      default: () => null,
    },
    isInitialRegistry: {
      type: Boolean,
      default: () => false,
    },
    showElegibilityWarning: {
      type: Boolean,
      default: () => false,
    },
  },
  methods: {
    getMeetingQuantities() {
      let items = [];
      for (let i = 1; i <= 10; i++) {
        items.push({ id: i, name: i.toString() });
      }
      return items;
    },
    radioElegibilityChange(isElegibility) {
      this.cleanImpedimentDependency();
      if (!isElegibility) {
        this.value.impediment = null;
      }
      if (this.showQuestionnaireForStudent) {
        this.canShowQuestinnarieLink(isElegibility);
      }
    },
    radioHealthSpecialistNecessaryChange(getIsHealthSpecialistNecessary) {
      //get to avoid broke anothers implements

      if (getIsHealthSpecialistNecessary == null) {
        this.value.isHealthSpecialistNecessary = null;
        this.value.participatedSpecialist = null;
      } else {
        this.value.isHealthSpecialistNecessary = getIsHealthSpecialistNecessary;
        this.value.participatedSpecialist = null;
      }
    },

    radioParticipatedSpecialistChange(isParticipatedSpecialist) {
      this.value.participatedSpecialist = isParticipatedSpecialist;
    },
    cleanImpedimentDependency() {
      this.cleanSectionImpedimentMultiple();
    },
    setImpedimentMultiple(impedimentMultipleSelected) {
      let item = {};
      item.id = impedimentMultipleSelected.id;
      item.association = null;
      item.title = impedimentMultipleSelected.name;

      return item;
    },
    //* Methods for Impediment Multiples
    btnAddClick(impediment) {
      let item = this.setImpedimentMultiple(impediment);

      if (item) {
        this.removeAddImpedimentMultipleItem(item);
      }
    },
    removeAddImpedimentMultipleItem(item) {
      this.value.impedimentMultiple.selectedItems.push(item);
      this.value.impedimentMultiple.impediment = null;
      this.determinationOptions.multipleImpediments = this.determinationOptions.multipleImpediments.filter(
        (x) => x.id !== item.id
      );
    },
    removeAddImpedimentMultipleItemSelected(item) {
      if (item.title) {
        item.name = item.title;
      }

      this.determinationOptions.multipleImpediments.push(item);

      this.value.impedimentMultiple.impediment = null;
      this.value.impedimentMultiple.selectedItems = this.value.impedimentMultiple.selectedItems.filter(
        (x) => x.id !== item.id
      );
    },
    cleanSectionImpedimentMultiple() {
      this.value.impedimentMultiple.impediment = null;
      this.value.impedimentMultiple.selectedItems = [];
    },
    // * End Methods for Impediment Multiples
    canShowQuestinnarieLink(canRequest) {
      if (!canRequest || this.calledFrom !== 'post-registry') {
        this.field.showQuestionnaireForStudent = false;
        return;
      }

      let uri =
        this.calledFrom == 'post-registry' && this.registry != null
          ? `/api/pei/${
              this.isInitialRegistry
                ? this.registry.regStudentId
                : this.registry.regStudentPostRegistryId
            }/${this.isInitialRegistry}/canShowQuestionnaireForStudent`
          : `/api/pei/canShowQuestionnaireForStudent/${this.$store.getters.SelectedStudent.studentId}`;

      this.$ApiGet(uri, false)
        .then((res) => {
          if (res.data) {
            this.field.showQuestionnaireForStudent =
              res.data.canShowQuestionnaireAlert;
            this.field.questionnaireStatus = res.data.status;
          }
        })
        .catch((err) => {
          this.ShowToast('Error', err.response.data.message, 'error');
        });
    },
    validateImpediments() {
      return this.$refs.ImpedimentsManager.validate();
    },
    async fetchImpedimentOptions() {
      try {
        let uri = `/api/triannualstudentevaluation/optionsbyimpediment`;
        uri = this.placementCommitteId
          ? `${uri}/${this.placementCommitteId}`
          : uri;
        const { data } = await this.$ApiGet(uri);

        this.impedimentsOptions = data;
      } catch (err) {
        this.ShowToast(
          'Error',
          'Ocurrio un problema recuperando informacion de los impedimentos',
          'error'
        );
      }
    },
  },
  async mounted() {
    //TODO Re-evaluated how fill this information
    this.canShowQuestinnarieLink(this.value.isElegibility);
    this.fetchImpedimentOptions();
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.impedimentsModel.severity = newValue.severity?.id
          ? newValue.severity
          : newValue.severityMultiple;
        this.impedimentsModel.isPartialHearing =
          newValue.partialHearing != null
            ? newValue.partialHearing
            : newValue.partialHearingMultiple;
        this.impedimentsModel.disorders = newValue.disorders?.id
          ? newValue.disorders
          : newValue.disordersMultiple;
      },
    },
    impedimentsOptions: {
      deep: true,
      handler(newValue) {
        this.value.impedimentsOptions = newValue;
      },
    },
    impedimentsModel: {
      deep: true,
      handler(newValue) {
        this.value.severity = newValue.severity;
        this.value.severityMultiple = newValue.severity;
        this.value.disorders = newValue.disorders;
        this.value.disordersMultiple = newValue.disorders;
        this.value.partialHearing = newValue.isPartialHearing;
        this.value.partialHearingMultiple = newValue.isPartialHearing;
      },
    },
    dateRegistration() {
      this.value.dateEligibility = this.dateRegistration;
    },
  },
  computed: {
    selectedImpedimentIds() {
      if (!this.value.impediment) return [];

      if (
        this.value.impediment.name != ImpedimentsEnum.MD_ImpedimentosMultiples
      )
        return [this.value.impediment.id];

      return [
        this.value.impediment.id,
        ...this.value.impedimentMultiple.selectedItems.map((x) => x.id),
      ];
    },
    canShowInterventionQuestionnaire() {
      const studentStatusId =
        this.studentRegistryStatus ??
        this.$store.getters.SelectedStudent.statusId;
      return (
        this.showInterventionQuestionnaire &&
        (studentStatusId == StudentStatuses.Evaluado ||
          (studentStatusId !== StudentStatuses.Evaluado &&
            this.field.questionnaireStatus == QuestionnarieStatus.Completado))
      );
    },
    hasDisorders() {
      return (
        this.value.impediment &&
        this.value.impediment.name === ImpedimentsEnum.AUT_Autismo
      );
    },
    hasMultipleImpediments() {
      return (
        this.value.impediment &&
        this.value.impediment.name === ImpedimentsEnum.MD_ImpedimentosMultiples
      );
    },
    multipleHasDisorders() {
      return (
        this.value.impedimentMultiple.impediment &&
        this.value.impedimentMultiple.impediment.name ===
          ImpedimentsEnum.AUT_Autismo
      );
    },
  },
};
</script>

<style src="@/assets/css/component/badge.css"></style>
<style scoped src="@/assets/css/component/radio-button.css" />

<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';

.separator {
  margin-top: 60px;
}
.student-search-alert-error.no-border {
  padding-left: 0;
}
.student-search-alert-error {
  background: none;
  border: none;
  padding: 0;
}
.dropdown-divider {
  margin-top: 20px;
  border-top: 2px solid #e9ecef;
}
.date-spacing {
  margin-right: 120px;
}
.error-margin-left {
  margin-left: -20px !important;
}
.bg-impediment {
  background: #fafbfa 0% 0% no-repeat padding-box;
  padding: 20px;
  margin: 20px;
}
.comment-spacing {
  margin-top: 65px;
}
.custom-chips >>> .chips-container {
  -webkit-box-shadow: none !important;
  margin-top: -30px;
}
.custom-chips >>> .chips {
  margin-left: -2px;
  margin-right: 50px;
}
.b {
  font-weight: bolder !important;
}
.tag-badge {
  display: inline-block;
  padding-right: 1em;
  height: 42px;
  background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0% 0%
    no-repeat padding-box;
  border-radius: 26px;
  cursor: pointer;
  margin-bottom: 10px;
}
.dot {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: inline-block;
  /* top: 268px; */
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  padding-top: 9px;
}
.orange-icon {
  color: #ff7058;
}
.orange-icon-badge-align {
  vertical-align: top;
}
.tag-badge-title {
  text-align: left;
  font: Bold 25px/30px Lato;
  letter-spacing: 0;
  color: #11291b;
  display: inline-block;
}
.item-pane {
  justify-content: space-between;
  min-width: 50%;
  box-shadow: -3px 1px 3px #00000029;
  margin-top: 10px;
  display: flex;
  border-bottom: solid 1px #e2e2e2;
}
.item-pane-info {
  background: #f2f2f2 0% 0% no-repeat padding-box;
  border-radius: 0px 0px 5px 5px;
  margin-bottom: 10px;
  font: 400 14px/17px Lato;
  letter-spacing: 0;
  color: #11291b;
  padding: 20px 10px 10px;
}
.item-pane-info p {
  margin-bottom: 10px;
}
.item-pane-info p i {
  margin-right: 8px;
  font-size: 16px;
}
.question-box {
  width: 65px;

  border-radius: 5px 5px 0px 0px;
  text-align: center;
  color: #ffffff;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  /* padding: 22px 12px; */
}
.border-box {
  border: 1px solid #e2e2e2;
  border-radius: 5px 5px 0px 0px;
  padding: 20px;
  width: 100%;
}
.border-box.gray {
  border-radius: 0px 0px 5px 0px;
  background: #f2f2f2 0% 0% no-repeat padding-box;
  border-top: none;
  padding: 20px 0;
}
/* .border-box.gray.error{
  background: #FFEBEB 0% 0% no-repeat padding-box;
  border: 1px solid #C9545E;
} */
.invalid {
  text-align: left;
  font: Lato, Regular;
  letter-spacing: 0;
  color: #ff5b5c;
  margin-bottom: 5px;
  font-weight: normal;
}
i.far.fa-check-circle {
  font-size: 24px;
  padding-right: 4px;
  line-height: 18px;
  margin-left: 4px;
}
.certificate-spacing {
  margin-right: -110px;
}
.terms-block {
  margin-left: -38px;
}

.dissability-options {
  ::v-deep {
    .col-12 {
      padding: 0;
    }
    .custom-radio {
      margin-bottom: 0.7rem;
    }
    .custom-control-label {
      font-weight: 700;
    }
  }
}

.eligiblity-determination__text {
  ::v-deep {
    label {
      color: #212529 !important;
      font-weight: 700;
    }
  }
}

.warning-message {
  background: map-get($colors, 'secondary-18');
  border: 1px solid map-get($colors, 'secondary-27');
}
</style>
